import { useState, useEffect, useCallback } from "react";
import { userService } from "../api/userService";
import { Resume } from "../types/resume";

export function useResumeManager(userId?: string) {
    //const { getAllUserResumes, createResume, updateResume, deleteResume } = userService();
    const [resumes, setResumes] = useState<Resume[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchResumes = useCallback(async () => {
        console.log("Fetching resumes for userId:", userId);
        if (!userId) return;
        setBusy(true);
        try {
            const { getAllUserResumes } = userService();
            const data = await getAllUserResumes(userId);
            setResumes(data);
        } catch (err: any) {
            setError("Failed to load resumes.");
        } finally {
            setBusy(false);
        }
    }, [userId]);

    const create = useCallback(async (resumeName: string, resumeText: string, isArchived: boolean, createdBy: string, isDraft: boolean) => {
        if (!userId) return null;
        setBusy(true);
        setError(null);
        try {
            const { createResume } = userService();
            const newResume = await createResume(userId, resumeName, resumeText, isArchived, createdBy, isDraft);
            await fetchResumes();
            return newResume;
        } catch (err: any) {
            setError(err?.response?.data ?? "Failed to create resume.");
            return null;
        } finally {
            setBusy(false);
        }
    }, [userId, fetchResumes]);

    const update = useCallback(async (resumeId: string, resumeName: string, resumeText: string, isArchived: boolean, createdBy: string, isDraft: boolean) => {
        setBusy(true);
        setError(null);
        try {
            const { updateResume } = userService();
            const updated = await updateResume(resumeId, resumeName, resumeText, isArchived, createdBy, isDraft);
            await fetchResumes();
            return updated;
        } catch (err: any) {
            setError(err?.response?.data ?? "Failed to update resume.");
            return null;
        } finally {
            setBusy(false);
        }
    }, [fetchResumes]);

    const remove = useCallback(async (resumeId: string) => {
        setBusy(true);
        setError(null);
        try {
            const { deleteResume } = userService();
            await deleteResume(resumeId);
            await fetchResumes();
        } catch (err: any) {
            setError(err?.response?.data ?? "Failed to delete resume.");
        } finally {
            setBusy(false);
        }
    }, [fetchResumes]);

    useEffect(() => {
        fetchResumes();
    }, [fetchResumes]);

    return { resumes, busy, error, create, update, remove, refetch: fetchResumes };
}